// API client del Padrón de Establecimientos (Semáforo de Comercio).
// Backend: /api/v1/admin/establishments/* (mcp_django, auth Bearer JWT).
// El org_id sale del JWT (nunca del cliente) → cada org ve SOLO su padrón.
// Mismo patrón (djangoFetch + ensureFreshToken) que citizen-reports.

import { ensureFreshToken } from '@/lib/tokenRefresh';
import { orgIdFromToken } from '@/lib/api_superadmin';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/establishments';

/** Estatus derivado por dimensión (normalizado en el borde por el backend). */
export type DimStatus =
  | 'cumple'
  | 'no_cumple'
  | 'en_tramite'
  | 'no_liberado'
  | 'no_aplica';

/** Semáforo global derivado (rojo si algo no cumple; amarillo si hay trámite). */
export type Semaforo = 'verde' | 'amarillo' | 'rojo';

/** Una fila del padrón, tal como la devuelve GET /semaforo (SemaforoRow). */
export interface SemaforoRow {
  id: number;
  nombre_comercial: string;
  giro: string;
  domicilio: string;
  semaforo: Semaforo | string;
  estatus_app: string;
  /** { proteccion_civil, ecologia, via_publica, uso_suelo, bomberos, gobernacion } */
  dimensiones: Record<string, DimStatus | string>;
}

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function djangoFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as { detail?: unknown }).detail ?? `Django ${res.status}: ${res.statusText}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json() as Promise<T>;
}

/** Página del padrón: la lista de la página + conteos globales (KPIs) + total del filtro. */
export interface SemaforoPage {
  total: number;                                   // filas del filtro q+estatus (para paginar)
  counts: { verde?: number; amarillo?: number; rojo?: number };  // sobre q (para KPIs)
  items: SemaforoRow[];
  /** true si el servidor no pagina y cortó el padrón (máx. 500): los conteos son parciales. */
  partial?: boolean;
}

/** Lista el padrón BUSCABLE y PAGINADO. `q` busca nombre/giro/domicilio en el servidor. */
export function listSemaforo(
  params: { q?: string; estatus?: string; giro?: string; limit?: number; offset?: number } = {},
): Promise<SemaforoPage> {
  const p = new URLSearchParams();
  if (params.q) p.set('q', params.q);
  if (params.estatus) p.set('estatus', params.estatus);
  if (params.giro) p.set('giro', params.giro);
  p.set('limit', String(params.limit ?? 50));
  p.set('offset', String(params.offset ?? 0));
  return djangoFetch<SemaforoPage | SemaforoRow[]>(`${BASE}/semaforo?${p.toString()}`).then(async (r) => {
    if (!Array.isArray(r)) return r;
    // Backend viejo: devuelve LISTA, ignora q/offset y corta en su límite. Se trae el
    // padrón completo una vez (sin filtros) y se busca/filtra/pagina en el cliente.
    return pageFromList(await padronCompleto(), params);
  });
}

/** Tope del servidor viejo en /semaforo (min(limit, 500)). */
const PADRON_MAX = 500;

// Caché corto del padrón completo (solo para backends sin paginar), POR ORG (la
// del JWT) y vaciado al cerrar sesión o cambiar de token: nunca se muestra el
// padrón de una org dentro de otra.
let _padron: { org: string; at: number; p: Promise<SemaforoRow[]> } | null = null;
if (typeof window !== 'undefined') {
  const vaciar = () => { _padron = null; };
  window.addEventListener('auth:logout', vaciar);
  window.addEventListener('auth:token-ready', vaciar);
}
function padronCompleto(): Promise<SemaforoRow[]> {
  const org = orgIdFromToken() || '';
  if (!_padron || _padron.org !== org || Date.now() - _padron.at > 60_000) {
    const entry = {
      org,
      at: Date.now(),
      p: djangoFetch<SemaforoRow[] | SemaforoPage>(`${BASE}/semaforo?limit=${PADRON_MAX}`)
        .then((r) => (Array.isArray(r) ? r : r.items)),
    };
    entry.p.catch(() => { if (_padron === entry) _padron = null; });
    _padron = entry;
  }
  return _padron.p;
}

/** Compatibilidad: backends que aún devuelven la LISTA completa (sin paginar) se
 *  adaptan aquí a la forma de página (búsqueda, conteos y corte en el cliente). */
export function pageFromList(
  rows: SemaforoRow[],
  params: { q?: string; estatus?: string; limit?: number; offset?: number },
): SemaforoPage {
  const norm = (x: unknown) => String(x ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const q = norm(params.q).trim();
  const byQ = q ? rows.filter((r) => [r.nombre_comercial, r.giro, r.domicilio].some((v) => norm(v).includes(q))) : rows;
  const counts: SemaforoPage['counts'] = {};
  for (const r of byQ) {
    const k = String(r.semaforo) as keyof SemaforoPage['counts'];
    if (k === 'verde' || k === 'amarillo' || k === 'rojo') counts[k] = (counts[k] ?? 0) + 1;
  }
  const filtered = params.estatus ? byQ.filter((r) => String(r.semaforo) === params.estatus) : byQ;
  const off = params.offset ?? 0;
  return { total: filtered.length, counts, items: filtered.slice(off, off + (params.limit ?? 50)), partial: rows.length >= PADRON_MAX };
}
